(() => {
  if (customElements.get('paws-discount-code')) return;

  const pendingStatusKey = 'pawsnova-discount-code-status';
  const cartRoute = (path) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/');
  };
  const getDiscountCodes = (cart) => Array.isArray(cart && cart.discount_codes) ? cart.discount_codes : [];
  const getApplicableCodes = (cart) => getDiscountCodes(cart).filter((discount) =>
    discount && discount.applicable !== false && discount.code
  );

  class PawsDiscountCode extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="discount"]');
      this.button = this.querySelector('button[type="submit"]');
      this.status = this.querySelector('[data-pn-discount-status]');
      if (!this.form || !this.input || !this.button || !this.status) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.input.addEventListener('input', () => this.clearStatus());
      this.restorePendingStatus();
      this.refreshAppliedCode();
    }

    setBusy(isBusy) {
      this.button.disabled = isBusy;
      this.button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isBusy);
    }

    clearStatus() {
      this.status.textContent = '';
      this.status.removeAttribute('data-tone');
      this.status.removeAttribute('role');
    }

    showStatus(message, tone = 'success') {
      this.status.textContent = message;
      this.status.dataset.tone = tone;
      this.status.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    }

    restorePendingStatus() {
      try {
        const pending = JSON.parse(sessionStorage.getItem(pendingStatusKey) || 'null');
        if (!pending || !pending.message) return;
        sessionStorage.removeItem(pendingStatusKey);
        this.showStatus(pending.message, pending.tone || 'success');
      } catch (_error) {
        sessionStorage.removeItem(pendingStatusKey);
      }
    }

    async fetchCart() {
      const response = await fetch(cartRoute('cart.js'), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }

    async refreshAppliedCode() {
      try {
        const cart = await this.fetchCart();
        const applied = getApplicableCodes(cart)[0];
        if (!applied || this.status.textContent) return;
        this.input.value = applied.code;
        this.showStatus(`${this.dataset.successMessage || 'Code applied'}: ${applied.code}`, 'success');
      } catch (_error) {
        // The input remains usable if this optional status request fails.
      }
    }

    async applyCode(code) {
      const currentCart = await this.fetchCart();
      const currentCodes = getApplicableCodes(currentCart).map((discount) => discount.code);
      if (!currentCodes.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())) currentCodes.push(code);

      const response = await fetch((window.routes && window.routes.cart_update_url) || cartRoute('cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: currentCodes.join(','),
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });

      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Discount request failed');
      return cart;
    }

    refreshDrawer(cart) {
      const cartDrawer = this.closest('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.renderContents !== 'function' || !cart.sections) return false;
      cartDrawer.renderContents(cart);
      return true;
    }

    async onSubmit(event) {
      event.preventDefault();
      const code = (this.input.value || '').trim();
      if (!code) {
        this.showStatus(this.dataset.requiredMessage || 'Enter a code first.', 'error');
        this.input.focus();
        return;
      }

      this.setBusy(true);
      this.clearStatus();

      try {
        const cart = await this.applyCode(code);
        const applied = getDiscountCodes(cart).find((discount) =>
          discount && discount.code && discount.code.toLowerCase() === code.toLowerCase()
        );

        if (!applied || applied.applicable === false) {
          this.showStatus(this.dataset.errorMessage || 'This code is invalid or does not apply to this cart.', 'error');
          return;
        }

        const message = `${this.dataset.successMessage || 'Code applied'}: ${applied.code}`;
        sessionStorage.setItem(pendingStatusKey, JSON.stringify({ message, tone: 'success' }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

        if (this.refreshDrawer(cart)) {
          document.querySelectorAll('paws-discount-code').forEach((component) => {
            if (typeof component.showStatus === 'function') component.showStatus(message, 'success');
          });
          sessionStorage.removeItem(pendingStatusKey);
        } else {
          window.location.reload();
        }
      } catch (_error) {
        this.showStatus(this.dataset.networkMessage || 'We could not apply the code. Please try again.', 'error');
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }
  }

  customElements.define('paws-discount-code', PawsDiscountCode);
})();
